import './LoiComparisonSummary.scss';
import React, { useState, useCallback, useEffect } from 'react';
import ReactSwitch from 'react-switch';
import { VvConnectFeesSection, getFeeRows } from './VvConnectFeesSection';
import { LoiComparisonFormDto, VendorContractPayoff } from '../../../../slices/marketplace/loi-comparison/models/loi-comparison-form.model';
import { isSfas, hasFairValueColumn } from '../../../../slices/marketplace/timeline/models/enums/level-of-service.enum';
import BuyerProfilePreviewPopup from '../../../../components/BuyerProfilePreviewPopup/BuyerProfilePreviewPopup';
import InfoTooltip from '../../../../components/info-tooltip/info-tooltip';
import SelectExecuteLoiPopup from '../SelectExecuteLoiPopup/SelectExecuteLoiPopup';
import ConfirmationModal from '../../../../components/Modal/ConfirmationModal';
import { useStateSelector } from '../../../../store/selectors';
import { startLoiResubmission, approveLoiSelection, declineLoiSelection, saveConsiderationRows, saveSfasTotalRetainerFee, fetchLoiComparison } from '../../../../slices/marketplace/loi-comparison/loi-comparison-api';
import { TypesOfConsiderationRow, areActiveConsiderationRowsComplete } from '../../../../slices/marketplace/loi-comparison/models/types-of-consideration-row.model';
import createNotification from '../../../../utils/createNotification';
import { DeepDiveDetailPanel } from './DeepDiveDetailPanel/DeepDiveDetailPanel';
import { getFirstDeepDiveRowLabel } from './DeepDiveDetailPanel/detail-config';
import { isLoiProcessCompleted } from '../../../../slices/marketplace/loi-comparison/models/loi-forms-process.model';
import { LoiVersionHistoryPopup } from '../LoiVersionHistoryPopup/LoiVersionHistoryPopup';
import { LoiHistoryPreviewPopup } from '../LoiHistoryPreviewPopup/LoiHistoryPreviewPopup';
import {
    BuyerSummaryCard,
    CovenantsValueColumn,
    COVENANT_ROWS,
    PRICE_ROWS,
} from './BuyerSummaryCard';
import { SelectedLoiBanner } from './SelectedLoiBanner';
import { TypesOfConsiderationSection } from './TypesOfConsiderationSection';
import { useBuyerSubTabs, BuyerSubTabs } from '../BuyerSubTabs';
import { useVendorContractsInit } from '../../../Admin/BuyerMarketplace/OpportunityDetails/LoiSubmissionPackage/useVendorContractsInit';
import { LoiComparisonVendorContractsTable } from './LoiComparisonVendorContractsTable';
import { buildConsiderationSummary } from './LoiComparisonRows';
import { EngagementProcess } from '../../../../slices/marketplace/timeline/models/engagement-process.model';
import { toSfasFeeParams } from '../../../../slices/marketplace/loi-comparison/consideration-fees';
import { getFairValuesByType, getFaceValuesByType } from '../../../../slices/marketplace/loi-comparison/consideration-fair-values';
import EditRetainerFeeModal from './EditRetainerFeeModal/EditRetainerFeeModal';
import { FloatingHorizontalScrollbar } from '../../../../components/FloatingHorizontalScrollbar/FloatingHorizontalScrollbar';

export const LabelsColumn = () => {
    return (
        <div className="loi-labels-column">
            <table>
                <thead>
                    <tr>
                        <th className="col-label section-title">Purchase Price</th>
                    </tr>
                    <tr className="sub-col-header-row">
                        <th className="col-label section-title">Consideration Summary</th>
                    </tr>
                </thead>
                <tbody>
                    {PRICE_ROWS.map(({ label, indented }) => (
                        <tr key={label}>
                            <td className={`col-label${indented ? ' col-label--indented' : ''}`}>{label}</td>
                        </tr>
                    ))}
                    <tr className="total-row">
                        <td className="col-label">Total Value</td>
                    </tr>
                </tbody>
            </table>

        </div>
    );
};

export const VvConnectFeesLabelsColumn = ({ isSfasEngagement = false }: { isSfasEngagement?: boolean }) => {
    return (
        <div className="loi-labels-column">
            <table className="vv-connect-fees-table">
                <thead>
                    <tr>
                        <th className="col-label section-title">VetValue Fee</th>
                    </tr>
                </thead>
                <tbody>
                    {getFeeRows(isSfasEngagement).map(({ label, key, isBold }) => (
                        <tr key={key} className={isBold ? 'total-row' : undefined}>
                            <td className="col-label">{label}</td>
                        </tr>
                    ))}
                    <tr className="total-row">
                        <td className="col-label">Portion Paid at Close</td>
                    </tr>
                    {isSfasEngagement && (
                        <tr className="total-row">
                            <td className="col-label">Projected Deferred Portion</td>
                        </tr>
                    )}
                </tbody>
            </table>
        </div>
    );
};

export const CovenantsLabelsColumn = () => {
    const covenantModifierClass = (kind: string) => {
        if (kind === 'subheader') {
            return ' col-label--subheader';
        }
        if (kind === 'group') {
            return ' col-label--group';
        }
        return ' col-label--indented';
    };

    return (
        <div className="loi-labels-column">
            <table>
                <thead>
                    <tr>
                        <th className="col-label section-title">Seller Restrictive Covenants</th>
                    </tr>
                </thead>
                <tbody>
                    {COVENANT_ROWS.map((row, idx) => (
                        <tr key={`${row.kind}-${row.label}-${idx}`}>
                            <td className={`col-label${covenantModifierClass(row.kind)}`}>{row.label}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
};

export interface LoiComparisonSummaryProps {
    items: LoiComparisonFormDto[];
    levelOfService: number;
    engagementProcess?: EngagementProcess | null;
    engagementId: number;
    isSeller: boolean;
    isBroker: boolean;
    isAdmin: boolean;
    loiComparisonEventId: string | null;
    isEventCompleted: boolean;
    fetchData: () => void;
    onSetItems: React.Dispatch<React.SetStateAction<LoiComparisonFormDto[]>>;
}

interface SelectedDetail {
    rowLabel: string;
    buyerId: number;
}

export const upsertPayoff = (
    payoffs: VendorContractPayoff[],
    saved: VendorContractPayoff,
): VendorContractPayoff[] => {
    const existingIndex = payoffs.findIndex((p) => p.tableRowId === saved.tableRowId);
    if (existingIndex === -1) {
        return [...payoffs, saved];
    }
    return payoffs.map((p, index) => (index === existingIndex ? saved : p));
};

export const LoiComparisonSummary = ({ items, levelOfService, engagementProcess = null, engagementId, onSetItems, isSeller, isBroker, isAdmin, loiComparisonEventId, isEventCompleted, fetchData }: LoiComparisonSummaryProps) => {
    const loiComparisonSummaryRef = React.useRef<HTMLDivElement>(null);
    const axios = useStateSelector((state) => state.core.axios);
    const vendorContracts = useVendorContractsInit({ engagementId, termForm: undefined, onChange: () => {}, axios });
    const [selectedBuyer, setSelectedBuyer] = useState<LoiComparisonFormDto | null>(null);
    const [collapsibleColumns, setCollapsibleColumns] = useState<Record<string, boolean>>({ face: false, fair: false });
    const [isDeepDive, setIsDeepDive] = useState(false);
    const [selectedDetail, setSelectedDetail] = useState<SelectedDetail | null>(null);
    const [popupBuyer, setPopupBuyer] = useState<LoiComparisonFormDto | null>(null);
    const [pendingResubmissionBuyerId, setPendingResubmissionBuyerId] = useState<number | null>(null);
    const [isResubmissionConfirmVisible, setIsResubmissionConfirmVisible] = useState(false);
    const [isResubmissionSubmitting, setIsResubmissionSubmitting] = useState(false);
    const [historyBuyerId, setHistoryBuyerId] = useState<number | null>(null);
    const [historyPreviewFormId, setHistoryPreviewFormId] = useState<string | null>(null);
    const [approvePopupBuyer, setApprovePopupBuyer] = useState<LoiComparisonFormDto | null>(null);
    const [declinePopupBuyer, setDeclinePopupBuyer] = useState<LoiComparisonFormDto | null>(null);
    const [isApproving, setIsApproving] = useState(false);
    const [isDeclining, setIsDeclining] = useState(false);
    const [isRetainerFeeModalVisible, setIsRetainerFeeModalVisible] = useState(false);
    const [isSavingRetainerFee, setIsSavingRetainerFee] = useState(false);
    const showFairValue = hasFairValueColumn(levelOfService);
    const sfasParams = toSfasFeeParams(engagementProcess);
    const anyBuyerAwaitingApproval = items.some(b => b.isAwaitingAdvisorApproval);
    const canSelectAtAll = isSeller && !isEventCompleted && !!loiComparisonEventId && !anyBuyerAwaitingApproval;
    const canEditRetainer = (isAdmin || isBroker) && isSfas(levelOfService);
    const currentMonthlyRetainerFee = items[0]?.vvConnectFees?.monthlyRetainerFees?.fee ?? null;

    const toggleColumn = (key: string) => setCollapsibleColumns(prev => ({ ...prev, [key]: !prev[key] }));

    const handleModeSwitchChange = (checkedTowardNetToOwner: boolean) => {
        setIsDeepDive(!checkedTowardNetToOwner);
    };

    const handleCellsClick = (rowLabel: string, buyerId: number) => {
        setSelectedDetail(prev => {
            if (prev?.rowLabel === rowLabel && prev?.buyerId === buyerId) {
                return null;
            }
            return { rowLabel, buyerId };
        });
    };

    const handleApprove = useCallback(() => {
        if (!approvePopupBuyer || !axios || !loiComparisonEventId) return;
        setIsApproving(true);
        approveLoiSelection(axios, engagementId, loiComparisonEventId, approvePopupBuyer.buyerId)
            .then(() => {
                setApprovePopupBuyer(null);
                fetchData();
            })
            .catch(() => createNotification('Could not approve selection. Please try again.', 'error'))
            .finally(() => setIsApproving(false));
    }, [approvePopupBuyer, axios, engagementId, loiComparisonEventId, fetchData]);

    const handleDecline = useCallback(() => {
        if (!declinePopupBuyer || !axios || !loiComparisonEventId) return;
        setIsDeclining(true);
        declineLoiSelection(axios, engagementId, loiComparisonEventId, declinePopupBuyer.buyerId)
            .then(() => {
                setDeclinePopupBuyer(null);
                fetchData();
            })
            .catch(() => createNotification('Could not decline selection. Please try again.', 'error'))
            .finally(() => setIsDeclining(false));
    }, [declinePopupBuyer, axios, engagementId, loiComparisonEventId, fetchData]);

    const handleConsiderationRowsSave = useCallback((
        buyerId: number,
        allRows: TypesOfConsiderationRow[],
        changedRows: TypesOfConsiderationRow[]
    ) => {
        if (!axios) return Promise.resolve();
        return saveConsiderationRows(axios, engagementId, buyerId, changedRows)
            .then(() => {
                onSetItems(prev =>
                    prev.map(item =>
                        item.buyerId === buyerId
                            ? { ...item, considerationRows: allRows }
                            : item
                    )
                );
            })
            .catch(() => createNotification('Could not save consideration row. Please try again.', 'error'));
    }, [axios, engagementId, onSetItems]);

    const handleSaveRetainerFee = (amount: number, reason: string) => {
        if (!axios) return;
        setIsSavingRetainerFee(true);
        saveSfasTotalRetainerFee(axios, engagementId, amount, reason)
            .then(() => fetchLoiComparison(axios, engagementId))
            .then((updatedItems) => {
                onSetItems(updatedItems);
                createNotification('Retainer fee amount saved successfully.', 'success');
                setIsRetainerFeeModalVisible(false);
            })
            .catch(() => createNotification('Failed to save retainer fee amount.', 'error'))
            .finally(() => setIsSavingRetainerFee(false));
    };

    const handleResubmissionToggle = (buyerId: number) => {
        setPendingResubmissionBuyerId(buyerId);
        setIsResubmissionConfirmVisible(true);
    };

    const handleResubmissionConfirm = async () => {
        if (!axios || pendingResubmissionBuyerId == null) return;
        setIsResubmissionSubmitting(true);
        try {
            await startLoiResubmission(axios, engagementId, pendingResubmissionBuyerId);
            onSetItems(prev =>
                prev.map(item =>
                    item.buyerId === pendingResubmissionBuyerId
                        ? { ...item, isResubmissionEnabled: true }
                        : item
                )
            );
            createNotification('Resubmission started successfully.', 'success');
            setIsResubmissionConfirmVisible(false);
        } catch {
            createNotification('Failed to start resubmission. Please try again.', 'error');
        } finally {
            setIsResubmissionSubmitting(false);
        }
    };

    const sortedItems = [...items].sort(
        (a, b) =>
            Number(b.isSelected || b.isAwaitingAdvisorApproval) -
            Number(a.isSelected || a.isAwaitingAdvisorApproval),
    );

    const { activeBuyerTab, buyerTabs, visibleItems, changeBuyerTab } =
        useBuyerSubTabs(sortedItems);
    const isDetailView = activeBuyerTab !== 'summary';
    const detailViewBuyer = isDetailView ? visibleItems[0] ?? null : null;

    useEffect(() => {
        if (!isDeepDive) {
            setSelectedDetail(null);
            return;
        }
        const buyer = visibleItems[0];
        const rowLabel = buyer
            ? getFirstDeepDiveRowLabel(buyer.form?.termForm?.purchasePriceSummary)
            : null;
        setSelectedDetail(rowLabel && buyer ? { rowLabel, buyerId: buyer.buyerId } : null);
    }, [isDeepDive, activeBuyerTab]);

    if (items.length === 0) {
        return null;
    }

    const selectedDetailBuyer = selectedDetail
        ? visibleItems.find((dto) => dto.buyerId === selectedDetail.buyerId) ?? null
        : null;

    const historyItem = historyBuyerId != null
        ? sortedItems.find((dto) => dto.buyerId === historyBuyerId) ?? null
        : null;

    const historyCompletedProcesses = historyItem
        ? [...historyItem.loiFormsProcesses]
            .filter(isLoiProcessCompleted)
            .sort((a, b) => (a.completedAt ?? '').localeCompare(b.completedAt ?? ''))
        : [];

    const historyPreviewItem = historyPreviewFormId != null
        ? sortedItems.find((dto) =>
            dto.loiFormsProcesses.some((p) => p.loiFormId === historyPreviewFormId)
        ) ?? null
        : null;

    return (
        <div className="loi-comparison-summary-wrapper">
            <BuyerSubTabs
                buyerTabs={buyerTabs}
                activeBuyerTab={activeBuyerTab}
                onChange={(tabId) => {
                    setSelectedDetail(null);
                    changeBuyerTab(tabId);
                }}
            />
            {detailViewBuyer != null && (
                <SelectedLoiBanner
                    item={detailViewBuyer}
                    onBuyerClick={() => setSelectedBuyer(detailViewBuyer)}
                    canSelectThisBuyer={canSelectAtAll && detailViewBuyer.isBuyerSubmitted}
                    onSelectClick={() => setPopupBuyer(detailViewBuyer)}
                    onResubmissionToggle={handleResubmissionToggle}
                />
            )}
            <div className="deep-dive-toolbar">
                <div className="switch-container">
                    <div className="switch-option">
                        <span>Deep Dive</span>
                        <InfoTooltip
                            text="Show details for each Purchase Price summary row. Click on (highlight) each form of consideration in the summary in order to see the details for that form of consideration below."
                            idText="loi-deep-dive-tooltip"
                        />
                    </div>
                    <ReactSwitch
                        checked={!isDeepDive}
                        onChange={handleModeSwitchChange}
                        height={22}
                        handleDiameter={16}
                        onColor="#535bd6"
                        offColor="#535bd6"
                        checkedIcon={false}
                        uncheckedIcon={false}
                    />
                    <div className="switch-option">
                        <span>Net to Owner</span>
                        <InfoTooltip
                            text="Show all sections of the LOI comparison. Provide fee payment selection for each form of consideration and projected values at time of receipt in order to create the appropriate analysis."
                            idText="loi-net-to-owner-tooltip"
                        />
                    </div>
                </div>
            </div>
            <div ref={loiComparisonSummaryRef} className={`loi-comparison-summary${isDetailView ? ' loi-comparison-summary--detail' : ''}`}>
            <div className="loi-consideration-block">
            <div className="summary-inner">
                <LabelsColumn />
                <div className="buyers-data-area">
                    {visibleItems.map((dto, index) => (
                        <React.Fragment key={dto.buyerId}>
                            {index > 0 && <div className="buyer-separator" />}
                            <BuyerSummaryCard
                                item={dto}
                                levelOfService={levelOfService}
                                sfasParams={sfasParams}
                                showFairValue={showFairValue}
                                collapsibleColumns={collapsibleColumns}
                                onToggleColumn={toggleColumn}
                                isDeepDive={isDeepDive}
                                selectedRowLabel={selectedDetail?.buyerId === dto.buyerId ? (selectedDetail?.rowLabel ?? null) : null}
                                onCellsClick={handleCellsClick}
                                onBuyerClick={() => setSelectedBuyer(dto)}
                                isEventCompleted={isEventCompleted}
                                canSelectThisBuyer={
                                    canSelectAtAll &&
                                    dto.isBuyerSubmitted &&
                                    areActiveConsiderationRowsComplete(dto.considerationRows)
                                }
                                onSelectClick={() => setPopupBuyer(dto)}
                                onResubmissionToggle={handleResubmissionToggle}
                                onViewHistory={() => setHistoryBuyerId(dto.buyerId)}
                                isBroker={isBroker}
                                onApproveClick={() => setApprovePopupBuyer(dto)}
                                onDeclineClick={() => setDeclinePopupBuyer(dto)}
                                isDetailView={isDetailView}
                            />
                        </React.Fragment>
                    ))}
                    <div className="buyer-separator" />
                </div>
            </div>
            {!isDeepDive && isDetailView && (
                <div className="summary-inner types-of-consideration-section">
                    <TypesOfConsiderationSection
                        items={visibleItems.map((dto) => ({
                            buyerId: dto.buyerId,
                            buyerName: dto.buyerName,
                            considerationRows: dto.considerationRows,
                            fairValueByType: getFairValuesByType(dto),
                            faceValueByType: getFaceValuesByType(dto),
                        }))}
                        canEditTiming={isSeller || isAdmin || isBroker}
                        canEditCashFlow={isSeller || isAdmin || isBroker}
                        canEditComment={isAdmin || isBroker}
                        levelOfService={levelOfService}
                        onSave={handleConsiderationRowsSave}
                        fullWidth
                    />
                </div>
            )}
            {!isDeepDive && isDetailView && detailViewBuyer && (
                <div className="loi-vendor-contracts-section">
                    <LoiComparisonVendorContractsTable
                        key={detailViewBuyer.buyerId}
                        vendorContracts={vendorContracts}
                        selectedRowIds={(detailViewBuyer.form?.termForm?.vendorContractRows ?? []).filter((row) => row.isAssumedByBuyer !== false).map((row) => row.tableRowId)}
                        loiFormId={detailViewBuyer.loiFormId}
                        payoffs={detailViewBuyer.vendorContractPayoffs ?? []}
                        onPayoffSaved={(saved) => onSetItems((prev) => prev.map((dto) =>
                            dto.buyerId === detailViewBuyer.buyerId
                                ? { ...dto, vendorContractPayoffs: upsertPayoff(dto.vendorContractPayoffs ?? [], saved) }
                                : dto))}
                    />
                </div>
            )}
            </div>
            {!isDeepDive && !isDetailView && (
                <div className="summary-inner covenants-section">
                    <CovenantsLabelsColumn />
                    <div className="buyers-data-area">
                        {visibleItems.map((dto, index) => (
                            <React.Fragment key={dto.buyerId}>
                                {index > 0 && <div className="buyer-separator" />}
                                <div className={`buyer-summary-card${dto.isSelected && !isDetailView ? ' buyer-summary-card--selected' : ''}`}>
                                    <CovenantsValueColumn
                                        item={dto}
                                        tooltipIdSuffix="-cov"
                                        onBuyerClick={() => setSelectedBuyer(dto)}
                                        onViewHistory={() => setHistoryBuyerId(dto.buyerId)}
                                    />
                                </div>
                            </React.Fragment>
                        ))}
                        <div className="buyer-separator" />
                    </div>
                </div>
            )}
            {!isDeepDive && (
                <div className="summary-inner vv-fees-section">
                    <VvConnectFeesLabelsColumn isSfasEngagement={isSfas(levelOfService)} />
                    <div className="buyers-data-area">
                        {visibleItems.map((dto, index) => {
                            const considerationSummary = buildConsiderationSummary(dto, levelOfService, sfasParams);
                            return (
                                <React.Fragment key={dto.buyerId}>
                                    {index > 0 && <div className="buyer-separator" />}
                                    <div className={`buyer-summary-card${dto.isSelected && !isDetailView ? ' buyer-summary-card--selected' : ''}`}>
                                        <VvConnectFeesSection
                                            fees={dto.vvConnectFees}
                                            portionPaidAtClose={considerationSummary.portionPaidAtClose}
                                            projectedTransactionValue={considerationSummary.projectedTransactionValue}
                                            projectedTransactionFee={considerationSummary.projectedTransactionFee}
                                            canEditRetainer={canEditRetainer}
                                            onEditRetainer={() => setIsRetainerFeeModalVisible(true)}
                                            levelOfService={levelOfService}
                                        />
                                    </div>
                                </React.Fragment>
                            );
                        })}
                        <div className="buyer-separator" />
                    </div>
                </div>
            )}
            </div>
            <FloatingHorizontalScrollbar targetRef={loiComparisonSummaryRef} />
            {isDeepDive && selectedDetail !== null && selectedDetailBuyer !== null && (
                <div className="deep-dive-details-section">
                    <div className="deep-dive-details-inner">
                        <div className="deep-dive-section-divider" />
                        <div className="deep-dive-detail-header">
                            <div className="deep-dive-detail-header__title">Details</div>
                            <div className="deep-dive-detail-header__subtitle">
                                <strong>Buyer:</strong> {selectedDetailBuyer.buyerName}
                            </div>
                            <div className="deep-dive-detail-header__subtitle">
                                <strong>Consideration:</strong> {selectedDetail.rowLabel}
                            </div>
                        </div>
                        <div className="deep-dive-section-divider" />
                        <DeepDiveDetailPanel
                            rowLabel={selectedDetail.rowLabel}
                            buyer={selectedDetailBuyer}
                        />
                    </div>
                </div>
            )}
            {selectedBuyer != null && (
                <BuyerProfilePreviewPopup
                    buyerId={selectedBuyer.buyerId}
                    close={() => setSelectedBuyer(null)}
                    showLoiTabs={true}
                    loiFormOverride={selectedBuyer.form}
                />
            )}
            {popupBuyer && loiComparisonEventId && (
                <SelectExecuteLoiPopup
                    isVisible={popupBuyer !== null}
                    onClose={() => setPopupBuyer(null)}
                    item={popupBuyer}
                    levelOfService={levelOfService}
                    sfasParams={sfasParams}
                    engagementId={engagementId}
                    eventId={loiComparisonEventId}
                    onSuccess={fetchData}
                />
            )}
            <ConfirmationModal
                isVisible={approvePopupBuyer !== null}
                setIsVisible={(visible: boolean) => { if (!visible) { setApprovePopupBuyer(null); } }}
                header="Approve LOI Selection"
                confirmationText={`Are you sure you want to approve the selection of ${approvePopupBuyer?.buyerName}?`}
                onConfirm={handleApprove}
                nextButtonText="Approve"
                cancelButtonText="Cancel"
                isLoading={isApproving}
                closeOnConfirm={false}
            />
            <ConfirmationModal
                isVisible={declinePopupBuyer !== null}
                setIsVisible={(visible: boolean) => { if (!visible) { setDeclinePopupBuyer(null); } }}
                header="Decline LOI Selection"
                confirmationText={`Are you sure you want to decline the selection of ${declinePopupBuyer?.buyerName}? The seller will be able to re-select.`}
                onConfirm={handleDecline}
                nextButtonText="Decline"
                cancelButtonText="Cancel"
                isLoading={isDeclining}
                closeOnConfirm={false}
            />
            <ConfirmationModal
                isVisible={isResubmissionConfirmVisible}
                setIsVisible={setIsResubmissionConfirmVisible}
                header="Enable Resubmission of Offer"
                confirmationText={<span>Are you sure you want to enable resubmission of offer for <strong>{items.find(i => i.buyerId === pendingResubmissionBuyerId)?.buyerName ?? 'this buyer'}</strong>?</span>}
                onConfirm={handleResubmissionConfirm}
                nextButtonText="Confirm"
                cancelButtonText="Cancel"
                isLoading={isResubmissionSubmitting}
                closeOnConfirm={false}
            />
            {historyItem && (
                <LoiVersionHistoryPopup
                    isVisible={historyPreviewFormId === null}
                    onClose={() => setHistoryBuyerId(null)}
                    buyerName={historyItem.buyerName}
                    processes={historyCompletedProcesses}
                    onViewVersion={setHistoryPreviewFormId}
                    engagementId={engagementId}
                />
            )}
            {isRetainerFeeModalVisible && (
                <EditRetainerFeeModal
                    isVisible={isRetainerFeeModalVisible}
                    setIsVisible={setIsRetainerFeeModalVisible}
                    currentAmount={currentMonthlyRetainerFee}
                    onSave={handleSaveRetainerFee}
                    isSaving={isSavingRetainerFee}
                />
            )}
            {historyPreviewItem && historyPreviewFormId && (
                <LoiHistoryPreviewPopup
                    isVisible={true}
                    onClose={() => setHistoryPreviewFormId(null)}
                    buyerItem={historyPreviewItem}
                    historicalLoiFormId={historyPreviewFormId}
                    engagementId={engagementId}
                    levelOfService={levelOfService}
                    sfasParams={sfasParams}
                />
            )}
        </div>
    );
};
